import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/classname';

interface Option {
  value: string;
  label: string;
}

interface SingleSelectDropdownProps {
  options: Option[];
  selectedValue?: string;
  onSelectionChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  options,
  selectedValue,
  onSelectionChange,
  placeholder = 'Select option',
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === selectedValue);

  const displayContent = () => {
    if (selectedOption) {
      return <span>{selectedOption.label}</span>;
    }
    return <span className="text-chariot-text-secondary">{placeholder}</span>;
  };

  const handleOptionSelect = (value: string) => {
    onSelectionChange(value);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'border-chariot-stroke text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary w-full rounded-md border px-3 py-2 text-left text-sm focus:ring-1 focus:outline-none',
          !disabled && 'bg-chariot-background-primary'
          // Disabled styling (including background) handled by DisabledFieldWrapper
        )}
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">{displayContent()}</div>
          <ChevronDownIcon
            className={cn(
              'text-chariot-text-secondary ml-2 size-4 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="border-chariot-stroke bg-chariot-background-primary absolute top-full right-0 left-0 z-50 mt-1 rounded-md border shadow-lg">
          <div className="max-h-60 overflow-auto p-1">
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionSelect(option.value)}
                className={cn(
                  'text-chariot-text-primary hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left text-sm',
                  selectedValue === option.value &&
                    'bg-chariot-background-secondary'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
